import { useUser } from "@/client/backend/user";
import { useCreateToken } from "@/client/backend/token";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { ReloadIcon, CopyIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { handleApiError } from "@/utils/api";
import { formatErrorMessage } from "@/utils/format";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { components } from "@/client/backend/zz_schema";

const CLI_SERVER_PORT = 32412;

export default function CLILogin() {
  const { user } = useUser();
  const { toast } = useToast();
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const { trigger: createToken } = useCreateToken(selectedOrgId || "loading");

  // If user is not logged in, redirect to login
  if (user === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <ReloadIcon className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user || user.organizations.length === 0) {
    return <Navigate to="/authn/login" replace />;
  }

  const handleAuthorize = async () => {
    if (!selectedOrgId) {
      toast({
        title: "Please select an organization",
        variant: "destructive",
      });
      return;
    }

    setIsAuthorizing(true);
    try {
      // Create a token for CLI access
      const tokenCreate: components["schemas"]["TokenCreate"] = {
        description: `CLI Token - ${new Date().toISOString().split('T')[0]}`,
        expirationMonths: 12, // 1 year expiration for CLI tokens
      };

      const result = await createToken(tokenCreate);
      
      if (!handleApiError(result, toast)) {
        return;
      }

      const token = result?.data?.token;
      if (!token) {
        throw new Error("No token received from server");
      }

      // Store the token for display
      setGeneratedToken(token);

      // Send token to CLI server
      try {
        const response = await fetch(`http://localhost:${CLI_SERVER_PORT}/authn/callback?api-key=${token}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`CLI server responded with status ${response.status}`);
        }

        setIsAuthorized(true);
        toast({
          title: "Authorization successful!",
          description: "You can now close this window and return to your CLI.",
        });

      } catch (cliError) {
        // If CLI server is not reachable, still show success with instructions
        setIsAuthorized(true);
        toast({
          title: "Token created successfully",
          description: "Please copy the token below and paste it into your CLI manually.",
        });
      }

    } catch (error) {
      toast({
        title: "Authorization failed",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsAuthorizing(false);
    }
  };

  if (isAuthorized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-green-600">Authorization Complete</CardTitle>
            <CardDescription>
            You can now close this window and return to your CLI.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Your CLI is now connected to the <strong>{user.organizations.find(org => org.id === selectedOrgId)?.displayName}</strong>.
              </p>
            </div>
            
            {generatedToken && (
              <div className="space-y-2">
                <Button
                  variant="outline"
                  onClick={() => setShowToken(!showToken)}
                  className="w-full justify-between p-3 h-auto"
                >
                  <span className="text-sm font-medium">
                    API Key
                  </span>
                  {showToken ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                </Button>
                
                {showToken && (
                  <div className="flex gap-2 animate-in slide-in-from-top-2 duration-200">
                    <div className="flex-1 p-3 bg-muted rounded-md border font-mono text-xs break-all">
                      {generatedToken}
                    </div>
                    <TooltipProvider>
                      <Tooltip open={copied}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              navigator.clipboard.writeText(generatedToken);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 2000);
                            }}
                          >
                            {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {copied ? "Copied!" : "Copy token"}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            )}
            
            <Button 
              onClick={() => window.close()} 
              className="w-full"
              variant="outline"
            >
              Close Window
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Authorize CLI Access</CardTitle>
          <CardDescription>
            Select an organization to authorize your CLI tool.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Organization
            </label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger>
                <SelectValue placeholder="Select an organization" />
              </SelectTrigger>
              <SelectContent>
                {user.organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.displayName || org.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              This will create an API key and configure your CLI to use it for accessing the selected organization.
            </p>
          </div>

          <Button 
            onClick={handleAuthorize}
            disabled={isAuthorizing || !selectedOrgId}
            className="w-full"
          >
            {isAuthorizing && (
              <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            {isAuthorizing ? "Authorizing..." : "Authorize CLI"}
          </Button>

          <div className="text-center">
            <Button 
              variant="link" 
              onClick={() => window.location.href = "/"}
              className="text-sm text-muted-foreground"
            >
              Cancel and go to dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
